import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  CaretRightIconSmall,
  DownloadIcon,
  SpinningLoader,
  Text,
  useSlideFadeTransition,
} from '@frontend/design-system';
import { LargeDownloadProgressType } from '../types';

interface Props {
  options: LargeDownloadProgressType;
}

export const DownloadProgressCard: FC<React.PropsWithChildren<Props>> = ({ options }) => {
  const { t } = useTranslation();
  const [isMinimized, setIsMinimized] = useState<boolean | undefined>();

  const transition = useSlideFadeTransition({
    active: options.isWaiting,
    direction: 'left',
    distance: '10%',
  });

  const getFileName = () => {
    const { fileExtension, fileName } = options;
    if (fileName) {
      return fileExtension ? `${fileName}.${fileExtension}` : fileName;
    } else {
      return 'file';
    }
  };

  return (
    <AnimatePresence>
      {transition.item && (
        <motion.div
          className='download-progress-card'
          key={transition.key}
          css={styles}
          animate={{
            ...transition.variants.visible,
            borderRadius: isMinimized ? 50 : 4,
            width: isMinimized ? 56 : 300,
          }}
          variants={transition.variants}
          initial='hidden'
          exit='exit'
          transition={transition.transition}
        >
          {isMinimized ? (
            <div className='mini-downloader' onClick={() => setIsMinimized(false)} role='button'>
              <SpinningLoader size='medium' />
              <DownloadIcon color='light' size={16} />
            </div>
          ) : (
            <>
              <SpinningLoader size='small' />
              <Text>{t('Preparing {{fileName}} to download', { fileName: getFileName() })}</Text>
              <CaretRightIconSmall color='light' onClick={() => setIsMinimized(true)} role='button' />
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const styles = css`
  align-items: center;
  background-color: ${theme.colors.neutral90};
  box-shadow: ${theme.shadows.heavy};
  color: ${theme.colors.white};
  display: flex;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};
  min-height: ${theme.spacing(7)};
  padding: ${theme.spacing(1)};

  p {
    color: inherit;
    line-height: 1;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 220px;
  }

  svg {
    cursor: pointer;
  }

  .mini-downloader {
    cursor: pointer;
    height: ${theme.spacing(5)};
    position: relative;
    width: ${theme.spacing(5)};

    > div {
      bottom: 0;
      position: absolute;
      right: 0;
    }

    > svg {
      bottom: ${theme.spacing(1.5)};
      position: absolute;
      right: ${theme.spacing(1.5)};
    }
  }
`;
